// eslint-disable-next-line no-unused-vars
import React, { useState } from 'react';
import { func, object } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import CreateOfferContent from 'Components/Wishlists/AddToModal/CreateOfferContent';
import useAnalytics from 'Hooks/useAnalytics';
import useOffers from 'Hooks/useOffers';
import useToastNotifications from 'Hooks/useToastNotifications';
import { EMAIL_REGEX } from 'Constants';
import { isProductUnavailableForOffer, normalizeWishlistProductPrice } from 'Utils/offers';

const getInitialValues = () => ({
  name: '',
  clientName: '',
  clientEmail: ''
});

const CreateOffer = ({
  product,
  sourceWishlist,
  onComplete,
  onCancel
}) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const toast = useToastNotifications();
  const { activeOfferId, createOffer } = useOffers();
  const [setActive, setSetActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const mustSetActive = !activeOfferId;
  const {
    formState: { errors: rhfErrors },
    getValues,
    register,
    trigger
  } = useForm({
    defaultValues: getInitialValues(),
    mode: 'onChange'
  });
  const maxLength = (value, message) => ({ value, message });
  const validators = {
    name: {
      validate: value => value.trim().length >= 3 || t('offers.offerNameLength'),
      maxLength: maxLength(255, t('offers.offerNameLength'))
    },
    clientName: {
      maxLength: maxLength(255, t('offers.clientNameLength'))
    },
    clientEmail: {
      maxLength: maxLength(255, t('offers.clientEmailLength')),
      pattern: { value: EMAIL_REGEX, message: t('offers.invalidClientEmail') }
    }
  };

  const handleSubmit = async event => {
    event?.preventDefault();
    if (!await trigger()) return;
    const values = getValues();
    const isProductFlow = Boolean(product?.productId || product?.id);
    const sourceProducts = isProductFlow ? [product] : sourceWishlist?.products || [];
    const offerProducts = sourceWishlist?.id
      ? sourceProducts.map(normalizeWishlistProductPrice)
      : sourceProducts;
    if (offerProducts.filter(Boolean).some(isProductUnavailableForOffer)) {
      analytics.track('add_to_destination_modal_create_offer_failed', {
        product_id: product?.productId || product?.id,
        source: isProductFlow ? 'product' : 'wishlist',
        reason: 'product_unavailable'
      });
      toast.addNotification({
        type: 'error',
        title: t('offers.productUnavailable')
      });
      return;
    }

    setLoading(true);
    analytics.track('add_to_destination_modal_create_offer_submitted', {
      product_id: product?.productId || product?.id,
      source: isProductFlow ? 'product' : 'wishlist',
      source_wishlist_id: sourceWishlist?.id || null,
      set_active: mustSetActive ? true : setActive
    });
    try {
      const offer = createOffer({
        ...values,
        setActive: mustSetActive ? true : setActive,
        name: values.name.trim(),
        clientName: values.clientName.trim(),
        clientEmail: values.clientEmail.trim(),
        sourceWishlist: sourceWishlist?.id
          ? { id: sourceWishlist.id, name: sourceWishlist.name }
          : null
      }, isProductFlow ? offerProducts.at(0) : offerProducts);
      toast.addNotification({
        type: 'success',
        title: t('offers.created'),
        description: offer.name
      });
      analytics.track('add_to_destination_modal_offer_created', {
        offer_id: offer.id,
        product_count: offer.items.length,
        source: isProductFlow ? 'product' : 'wishlist',
        set_active: mustSetActive ? true : setActive
      });
      onComplete(offer);
    } catch (e) {
      analytics.track('add_to_destination_modal_create_offer_failed', {
        product_id: product?.productId || product?.id,
        source: isProductFlow ? 'product' : 'wishlist'
      });
      toast.addNotification({
        type: 'error',
        title: e?.info?.errors?.at(0)?.message || e.message || t('offers.createFailed')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <CreateOfferContent
      product={ product }
      validators={ validators }
      register={ register }
      rhfErrors={ rhfErrors }
      setActive={ setActive }
      loading={ loading }
      mustSetActive={ mustSetActive }
      onSetActive={ checked => {
        if (!mustSetActive) {
          analytics.track('add_to_destination_modal_set_active_changed', {
            checked
          });
          setSetActive(checked);
        }
      } }
      onSubmit={ handleSubmit }
      onCancel={ onCancel }
    />
  );
};

CreateOffer.propTypes = {
  product: object,
  sourceWishlist: object,
  onComplete: func,
  onCancel: func
};

export default CreateOffer;
